// M004-F0769 (admin member page) / M004-F0770 (customer statement page) / M004-F0771 (refusals and outcomes), WebUI #246 phase 2.
// A lazy "View advice note" disclosure: nothing is requested until it is opened. The page renders what the Ledger's advice_note.preview
// answers; it never sends invoice fields and never sends target_user_id in the customer scope.
import React, { useCallback, useRef, useState } from 'react';
import StepUpDialog from '../auth/StepUpDialog';
import { previewAdviceNote } from '../../services/adviceNoteApi';
import { getStepUpChallengeFromError } from '../../utils/stepUp';
import { groupLinesByMonth, pdfBlobFromPayload, pillLabel, refusalMessage, totalsRows, validatePeriod } from './adviceStatementModel';
import './AdviceStatementPanel.css';

const defaultFormatMoney = (minor, currency = 'GBP') => new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'GBP' }).format(Number(minor || 0) / 100);
const FALLBACK_REFUSAL = 'The advice note could not be shown.';
const CANCELLED = 'Verification was cancelled.';

export default function AdviceStatementPanel({ orgId, scope = 'customer', targetUserId, formatMoney = defaultFormatMoney }) {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState(null);
  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');
  const [periodError, setPeriodError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stepUpChallenge, setStepUpChallenge] = useState(null);
  // Newest request wins: an answer whose sequence number is no longer the latest is ignored.
  const seqRef = useRef(0);
  // The request to re-send once after a successful step-up.
  const pendingRef = useRef(null);
  const regionId = `advice-statement-region-${String(orgId ?? 'org').replace(/[^A-Za-z0-9_-]/g, '')}`;

  // Customer scope never forwards a target, whatever the prop says. Admin scope names the member.
  const send = useCallback(async (period, { retry = false } = {}) => {
    const seq = seqRef.current + 1;
    seqRef.current = seq;
    pendingRef.current = { period, retried: retry };
    setLoading(true);
    setError('');
    const target = scope === 'admin' ? targetUserId : undefined;
    try {
      const next = await previewAdviceNote({ orgId, period, targetUserId: target });
      if (seq !== seqRef.current) return;
      pendingRef.current = null;
      setPayload(next);
      setFromInput(next?.period?.from || '');
      setToInput(next?.period?.to || '');
      setLoading(false);
    } catch (cause) {
      if (seq !== seqRef.current) return;
      // Same rule as LedgerQueueSections: a 403 is a refusal and never opens the step-up dialog.
      const challenge = Number(cause?.response?.status) === 403 ? null : getStepUpChallengeFromError(cause);
      if (challenge?.id && !retry) {
        setLoading(false);
        setStepUpChallenge(challenge);
        return;
      }
      pendingRef.current = null;
      setLoading(false);
      setError(refusalMessage(cause) || FALLBACK_REFUSAL);
    }
  }, [orgId, scope, targetUserId]);

  const toggle = () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    // The first open (and any open after a failed first load) makes the one request, with no period.
    if (nextOpen && !payload && !loading) send(undefined);
  };

  const update = (event) => {
    event?.preventDefault?.();
    const message = validatePeriod(fromInput, toInput);
    if (message) { setPeriodError(message); return; }
    setPeriodError('');
    send({ from: fromInput, to: toInput });
  };

  const completeStepUp = () => {
    const pending = pendingRef.current;
    setStepUpChallenge(null);
    if (pending) send(pending.period, { retry: true });
  };

  const cancelStepUp = () => {
    pendingRef.current = null;
    setStepUpChallenge(null);
    setError(CANCELLED);
  };

  const download = () => {
    if (!payload) return;
    const blob = pdfBlobFromPayload(payload);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = payload.filename || 'advice-note.pdf';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const lines = payload?.lines || [];
  const currency = payload?.currency || 'GBP';
  const groups = payload ? groupLinesByMonth(lines) : [];
  const credit = payload?.credit?.rows || [];
  const totals = payload ? totalsRows(payload) : [];
  const busy = loading;

  return (
    <section className="advice-statement" data-testid="advice-statement-panel">
      <button type="button" className="advice-statement-toggle" data-testid="advice-statement-toggle"
        aria-expanded={open} aria-controls={regionId} onClick={toggle}>View advice note</button>
      {open ? (
        <div id={regionId} className="advice-statement-body" role="region" aria-label="Advice note" aria-busy={busy ? 'true' : 'false'} data-testid="advice-statement-region">
          <form className="advice-statement-controls" data-testid="advice-statement-form" onSubmit={update} noValidate>
            <label className="advice-statement-field">
              <span>From</span>
              <input type="month" name="advice-statement-from" data-testid="advice-statement-from" value={fromInput}
                onChange={(event) => setFromInput(event.target.value)} />
            </label>
            <label className="advice-statement-field">
              <span>To</span>
              <input type="month" name="advice-statement-to" data-testid="advice-statement-to" value={toInput}
                onChange={(event) => setToInput(event.target.value)} />
            </label>
            <button type="submit" className="advice-statement-button" data-testid="advice-statement-update" disabled={busy}>Update</button>
            <button type="button" className="advice-statement-button" data-testid="advice-statement-download" disabled={busy || !payload} onClick={download}>Download PDF</button>
          </form>
          {periodError ? <p className="advice-statement-error" role="alert" data-testid="advice-statement-period-error">{periodError}</p> : null}
          <p className="advice-statement-status" role="status" aria-live="polite" data-testid="advice-statement-status">{busy ? 'Loading advice note…' : ''}</p>
          {error ? <p className="advice-statement-error" role="alert" data-testid="advice-statement-error">{error}</p> : null}
          {payload ? (
            <div className="advice-statement-content" data-testid="advice-statement-content">
              {error ? <p className="advice-statement-muted" data-testid="advice-statement-last-loaded">Last loaded period: {payload.period?.from} to {payload.period?.to}</p> : null}
              {payload.face_text ? <p className="advice-statement-face" data-testid="advice-statement-face">{payload.face_text}</p> : null}
              {groups.length === 0 ? <p className="advice-statement-muted" data-testid="advice-statement-empty">Nothing is expected for this period.</p> : null}
              {groups.map((group) => (
                <div className="advice-statement-month" key={group.month} data-testid="advice-statement-month">
                  <h4 className="advice-statement-month-heading">
                    <span>{group.heading}</span>
                    <span className="advice-statement-subtotal" data-testid="advice-statement-subtotal">{formatMoney(group.subtotalMinor, currency)}</span>
                  </h4>
                  <ul className="advice-statement-lines">
                    {group.lines.map((line, index) => (
                      <li key={`${line.date}-${index}`} className={`advice-statement-line${line.counted === false ? ' is-muted' : ''}`} data-testid="advice-statement-line">
                        <span className="advice-statement-line-date">{line.date}</span>
                        <span className="advice-statement-line-desc">{line.description}</span>
                        <span className={`ledger-queue-pill pill-${line.pill}`} data-testid="advice-statement-pill">{pillLabel(line)}</span>
                        {line.counted === false ? <span className="advice-statement-muted" data-testid="advice-statement-not-in-total">Not in total</span> : null}
                        <span className="advice-statement-line-amount">{formatMoney(line.amount_minor, currency)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {credit.length ? (
                <div className="advice-statement-credit" data-testid="advice-statement-credit">
                  <h4 className="advice-statement-month-heading">Credit</h4>
                  <ul className="advice-statement-lines">
                    {credit.map((row, index) => (
                      <li key={row.credit_note_id || index} className="advice-statement-line" data-testid="advice-statement-credit-row">
                        <span className="advice-statement-line-desc">{row.credit_note_number || '—'}</span>
                        <span className="advice-statement-line-date">{row.date || '—'}</span>
                        <span className="advice-statement-line-amount">{formatMoney(row.amount_minor, currency)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <dl className="advice-statement-totals" data-testid="advice-statement-totals">
                {totals.map((row) => (
                  <div className="advice-statement-total" key={row.key} data-testid={`advice-statement-total-${row.key}`}>
                    <dt>{row.label}</dt>
                    <dd>{formatMoney(row.minor, currency)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : null}
        </div>
      ) : null}
      {stepUpChallenge ? (
        <StepUpDialog challenge={stepUpChallenge} title="Verify advice note" description="Confirm your identity before viewing this advice note."
          onComplete={completeStepUp} onCancel={cancelStepUp} />
      ) : null}
    </section>
  );
}
